/* tsc-fx — pointer-reactive foil on product cards, magnetic call-to-action.
 *
 * Three rules this file follows, because the theme already has 19 animations
 * running and adding a twentieth badly would cost more than it gains:
 *
 *   1. ONE listener, not one per card. A grid can hold forty tiles; forty
 *      pointermove listeners is forty closures firing on every mouse move.
 *      This delegates from the document and finds the card by closest().
 *   2. Nothing touches the DOM outside a rAF. pointermove fires far faster
 *      than the screen refreshes, so writes are coalesced to one per frame --
 *      otherwise every move forces a style recalculation the frame throws away.
 *   3. It only ever writes custom properties. All the visual work is in
 *      tsc-fx.css, so a page that never runs this file renders the finished
 *      resting state rather than something half-applied.
 */
(function () {
  "use strict";

  var reduce = window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)");
  if (reduce && reduce.matches) return;

  /* A coarse pointer is a finger: there is no hover, and a tilt that only
   * appears mid-tap is noise. Touch still gets the foil while dragging across
   * a card, which is handled further down; it just does not get the lean. */
  var fine = window.matchMedia && matchMedia("(pointer: fine)").matches;

  var frame = null, queue = [];
  function write(el, props) {
    queue.push([el, props]);
    if (frame) return;
    frame = requestAnimationFrame(function () {
      frame = null;
      var q = queue; queue = [];
      q.forEach(function (job) {
        for (var k in job[1]) job[0].style.setProperty(k, job[1][k]);
      });
    });
  }

  /* ── foil ──────────────────────────────────────────────────────────── */
  /* The custom properties are written to .pc-art, NOT to .pc.
   *
   * They are registered `inherits: true` because ::before has to read them,
   * and an inherited property changing invalidates style for the element's
   * whole subtree. On .pc that subtree is the art, the badges, the title, the
   * price, the stock line and the add button -- all recalculated on every
   * pointer move, for every card swept across. Measured on a 4x-throttled
   * phone across nine cards that was 430ms of style recalculation. Writing to
   * .pc-art narrows the subtree to one element and its pseudo. */
  function foil(e) {
    var card = e.target.closest && e.target.closest(".pc");
    if (!card) return;
    var art = card.querySelector(".pc-art");
    if (!art) return;
    var box = card.getBoundingClientRect();
    if (!box.width || !box.height) return;
    var x = Math.max(0, Math.min(100, ((e.clientX - box.left) / box.width) * 100));
    var y = Math.max(0, Math.min(100, ((e.clientY - box.top) / box.height) * 100));
    /* Angles are computed here rather than in the stylesheet because CSS cannot
     * turn a percentage into an angle -- rotateX(calc(50% - ...)) is invalid and
     * silently voids the entire transform. Numbers out, units applied in CSS.
     * 8deg is the ceiling: past about ten the art stops reading as a card
     * catching light and starts reading as a page bug. */
    write(art, {
      "--fx-x": x.toFixed(1) + "%",
      "--fx-y": y.toFixed(1) + "%",
      "--fx-rx": ((50 - y) * 0.16).toFixed(2),
      "--fx-ry": ((x - 50) * 0.16).toFixed(2),
      "--fx-on": "1"
    });
  }
  function unfoil(e) {
    var card = e.target.closest && e.target.closest(".pc");
    var art = card && card.querySelector(".pc-art");
    if (art) write(art, { "--fx-on": "0" });
  }

  document.addEventListener("pointermove", foil, { passive: true });
  document.addEventListener("pointerleave", unfoil, { passive: true, capture: true });
  /* A finger leaving the glass fires pointercancel, not pointerleave. Without
   * this a tile stays lit after the touch ends. */
  document.addEventListener("pointercancel", unfoil, { passive: true, capture: true });
  document.addEventListener("pointerup", function (e) {
    if (e.pointerType !== "mouse") unfoil(e);
  }, { passive: true });

  /* Keyboard users get the same thing on focus -- the card is a link, so it is
   * reachable by tab, and lighting it is the same affordance a hover gives. */
  document.addEventListener("focusin", function (e) {
    var card = e.target.closest && e.target.closest(".pc");
    var art = card && card.querySelector(".pc-art");
    if (art) write(art, { "--fx-x": "50%", "--fx-y": "36%", "--fx-rx": "2.24", "--fx-ry": "0", "--fx-on": "1" });
  });
  document.addEventListener("focusout", unfoil);

  /* ── magnetic call to action ───────────────────────────────────────── */
  if (!fine) return;

  var MAG = ".btn-game, .btn.gold, .pc-add";
  var PULL = 0.28, MAX = 9;          /* px of lean, deliberately small */

  Array.prototype.forEach.call(document.querySelectorAll(MAG), function (el) {
    el.setAttribute("data-mag", "");
  });

  /* The offset is measured from where the button RESTS, not from where it
   * currently is.
   *
   * This is the whole difficulty of a magnetic button and it is not obvious.
   * getBoundingClientRect() reports the element after its translate, so
   * leaning it toward the cursor moves the rect, which changes the next delta,
   * which moves it again -- the button hunts around under a cursor that is not
   * even moving. Playwright caught this as "element is not stable" before a
   * person ever saw it.
   *
   * Subtracting the translate we ourselves applied gives the resting centre,
   * which is fixed, so a stationary cursor produces one stationary offset. */
  function restCentre(el, b) {
    var cs = getComputedStyle(el);
    var mx = parseFloat(cs.getPropertyValue("--mag-x")) || 0;
    var my = parseFloat(cs.getPropertyValue("--mag-y")) || 0;
    return { x: b.left + b.width / 2 - mx, y: b.top + b.height / 2 - my };
  }

  document.addEventListener("pointermove", function (e) {
    if (e.pointerType !== "mouse") return;
    var el = e.target.closest && e.target.closest(MAG);
    if (!el) return;
    var b = el.getBoundingClientRect();
    var c = restCentre(el, b);
    el.setAttribute("data-mag-live", "");
    write(el, {
      "--mag-x": Math.max(-MAX, Math.min(MAX, (e.clientX - c.x) * PULL)).toFixed(2),
      "--mag-y": Math.max(-MAX, Math.min(MAX, (e.clientY - c.y) * PULL)).toFixed(2)
    });
  }, { passive: true });

  document.addEventListener("pointerout", function (e) {
    var el = e.target.closest && e.target.closest(MAG);
    if (!el || (e.relatedTarget && el.contains(e.relatedTarget))) return;
    el.removeAttribute("data-mag-live");
    write(el, { "--mag-x": "0", "--mag-y": "0" });
  }, { passive: true });
  /* ── add to cart ───────────────────────────────────────────────────
   * The one moment on the whole store worth celebrating, and it had nothing.
   *
   * Hooked on the cart form's submit rather than on a themed button, because
   * the add-to-cart control lives in Horizon's own buy-buttons block and this
   * theme does not modify core Horizon files. Any form posting to /cart/add
   * qualifies, so this keeps working if that block is ever swapped out.
   *
   * Particles are real elements animated through the Web Animations API: at
   * eighteen of them that is cheaper than standing up a canvas and a render
   * loop for something that lasts 700ms, and each one removes itself on
   * finish so nothing accumulates in the DOM.
   */
  var GOLD = ["#FFB627", "#FFD97E", "#FF2D6F", "#2DE2C5", "#FFF6DC"];
  function burst(x, y) {
    var n = 18;
    for (var i = 0; i < n; i++) {
      var pip = document.createElement("span");
      pip.className = "fx-pip";
      pip.style.left = x + "px";
      pip.style.top = y + "px";
      pip.style.background = GOLD[i % GOLD.length];
      var size = 5 + Math.random() * 6;
      pip.style.width = pip.style.height = size.toFixed(1) + "px";
      document.body.appendChild(pip);

      var angle = (Math.PI * 2 * i) / n + Math.random() * 0.4;
      var dist = 42 + Math.random() * 74;
      var anim = pip.animate([
        { transform: "translate(-50%,-50%) translate(0,0) scale(1)", opacity: 1 },
        { transform: "translate(-50%,-50%) translate(" + (Math.cos(angle) * dist).toFixed(1) + "px,"
            + (Math.sin(angle) * dist + 26).toFixed(1) + "px) scale(0.2)", opacity: 0 }
      ], { duration: 620 + Math.random() * 220, easing: "cubic-bezier(.16,1,.3,1)", fill: "forwards" });
      anim.onfinish = function (el) { return function () { el.remove(); }; }(pip);
    }
  }

  document.addEventListener("submit", function (e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf("/cart/add") === -1) return;
    var btn = form.querySelector('[type="submit"]') || form;
    var b = btn.getBoundingClientRect();
    if (b.width) burst(b.left + b.width / 2, b.top + b.height / 2);
  }, true);
})();